import { useState } from 'react'

import { USERS_ACCESS_ROLE_LABEL_KEYS } from '../../constants/usersAccess'
import type { UsersAccessInviteErrors } from '../../hooks/useUsersAccessInvite'
import { useUsersAccessMutations } from '../../hooks/useUsersAccessMutations'
import type { UsersAccessUserRowI } from '../../types/usersAccess'
import { useTranslation } from 'react-i18next'

import * as EmailValidator from 'email-validator'

import { Modal, Stack, TextInput } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { tVar } from '@/utils/i18n'

interface EditUsersAccessUserModalIProps {
  colorScheme: ColorScheme
  isOpen: boolean
  onClose: () => void
  row: UsersAccessUserRowI
}

export const EditUsersAccessUserModal = ({
  colorScheme,
  isOpen,
  onClose,
  row,
}: EditUsersAccessUserModalIProps) => {
  const { t } = useTranslation()
  const { editUser, isUpdatingUser } = useUsersAccessMutations(colorScheme)
  const [values, setValues] = useState({
    email: row.email,
    firstName: row.firstName,
    lastName: row.lastName,
  })
  const [errors, setErrors] = useState<UsersAccessInviteErrors>({})
  const title =
    row.roles.length === 1
      ? tVar(t, 'settings:rebrand_users_edit_role_title', {
          role: t(USERS_ACCESS_ROLE_LABEL_KEYS[row.roles[0]]),
        })
      : t('settings:rebrand_users_edit_title')

  const setField = (field: keyof typeof values, value: string) => {
    setValues((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: undefined }))
  }

  const save = async () => {
    const nextErrors: UsersAccessInviteErrors = {}
    if (!values.firstName.trim()) {
      nextErrors.firstName = t('settings:rebrand_users_first_name_required')
    }
    if (!values.lastName.trim()) {
      nextErrors.lastName = t('settings:rebrand_users_last_name_required')
    }
    if (!EmailValidator.validate(values.email.trim())) {
      nextErrors.email = t('settings:rebrand_users_email_invalid')
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    const didSave = await editUser(row, {
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
    })
    if (didSave) onClose()
  }

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      closeOnEsc={!isUpdatingUser}
      closeOnOverlayClick={!isUpdatingUser}
      colorScheme={colorScheme}
      isCloseHidden={isUpdatingUser}
      isOpen={isOpen}
      isSaveLoading={isUpdatingUser}
      maxW='600px'
      onClose={onClose}
      onSave={save}
      saveButtonLabel={t('settings:rebrand_users_edit_submit')}
      showCancelButton={!isUpdatingUser}
      size='compact'
      title={title}
    >
      <Stack gap='16px'>
        <TextInput
          colorScheme={colorScheme}
          data-testid='EditUsersAccessUserModal:firstName'
          hasWarning={Boolean(errors.firstName)}
          inputProps={{ autoComplete: 'given-name' }}
          label={t('settings:rebrand_users_first_name_label')}
          onValueChange={(value) => setField('firstName', value)}
          placeholder={t('settings:rebrand_users_first_name_placeholder')}
          value={values.firstName}
          warningText={errors.firstName}
          width='100%'
        />
        <TextInput
          colorScheme={colorScheme}
          data-testid='EditUsersAccessUserModal:lastName'
          hasWarning={Boolean(errors.lastName)}
          inputProps={{ autoComplete: 'family-name' }}
          label={t('settings:rebrand_users_last_name_label')}
          onValueChange={(value) => setField('lastName', value)}
          placeholder={t('settings:rebrand_users_last_name_placeholder')}
          value={values.lastName}
          warningText={errors.lastName}
          width='100%'
        />
        <TextInput
          colorScheme={colorScheme}
          data-testid='EditUsersAccessUserModal:email'
          hasWarning={Boolean(errors.email)}
          inputProps={{ autoComplete: 'email', type: 'email' }}
          label={t('settings:rebrand_users_email_label')}
          onValueChange={(value) => setField('email', value)}
          placeholder={t('settings:rebrand_users_email_placeholder')}
          value={values.email}
          warningText={errors.email}
          width='100%'
        />
      </Stack>
    </Modal>
  )
}
